import { createContext, useContext, useEffect, useState } from "react";
import { ScheduledMatch, TeamStats } from "../types";
import { useActiveMatch } from "./useActiveMatch";

type TeamStatsContextType = {
  match: ScheduledMatch;
  loading: boolean;
  data: TeamStats | null;
  error: string | null;
};

const TeamStatsContext = createContext<TeamStatsContextType>({
  match: null,
  loading: false,
  data: null,
  error: null,
});

export function TeamStatsProvider({ children }: { children: React.ReactNode }) {
  const { match } = useActiveMatch();

  const [data, setData] = useState<TeamStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/stats/${match.id}.json`);
      if (!response.ok) {
        throw new Error(`Failed to fetch stats for team ID ${match.id}`);
      }
      const json = await response.json();
      setData(json as TeamStats);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!match.venueId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    fetchPreferences();
  }, [match.venueId]);

  return (
    <TeamStatsContext.Provider value={{ match, loading, data, error }}>
      {children}
    </TeamStatsContext.Provider>
  );
}

export function useTeamStats() {
  return useContext(TeamStatsContext);
}
